import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Card, Input, Select } from '@rallyshift/design-system'
import api from '../../api/client'
import { ROOT_DOMAIN, isPlausibleSlug } from '../../api/tenant'

const PLANS = [
  { value: 'trial', label: 'Proef' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
]

const PlatformOrgNewPage: React.FC = () => {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerPassword, setOwnerPassword] = useState('')
  const [ownerFirstName, setOwnerFirstName] = useState('')
  const [ownerLastName, setOwnerLastName] = useState('')
  const [plan, setPlan] = useState('trial')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const derivedSlug = slug.trim().toLowerCase() || name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const candidate = derivedSlug
    if (!isPlausibleSlug(candidate)) {
      setError('Het webadres mag alleen kleine letters, cijfers en streepjes bevatten.')
      return
    }

    setSaving(true)
    setError('')
    try {
      const created = await api.platform.createOrganization({
        name: name.trim(),
        slug: candidate,
        owner_email: ownerEmail.trim(),
        owner_password: ownerPassword || undefined,
        owner_first_name: ownerFirstName.trim() || undefined,
        owner_last_name: ownerLastName.trim() || undefined,
        plan,
      })
      navigate(`/platform/organizations/${created.id}`, { replace: true })
    } catch (err: any) {
      setError(err.message || 'Aanmaken is niet gelukt.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-display-md text-text-primary">Nieuwe organisatie</h1>
        <p className="mt-1 text-md text-text-secondary">
          Maakt de organisatie en zet iemand als eigenaar. Als dat adres al een account
          heeft, wordt die eigenaar; anders is een wachtwoord verplicht.
        </p>
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <form onSubmit={submit} className="space-y-4">
          <Input
            label="Naam"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Webadres"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={derivedSlug || 'jouw-organisatie'}
            hint={`${derivedSlug || 'jouw-organisatie'}.${ROOT_DOMAIN}`}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <Select label="Plan" value={plan} onChange={(e) => setPlan(e.target.value)} options={PLANS} />
          <Input
            label="E-mail van de eigenaar"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            required
          />
          <Input
            label="Wachtwoord van de eigenaar"
            type="password"
            value={ownerPassword}
            onChange={(e) => setOwnerPassword(e.target.value)}
            hint="Alleen nodig als dit adres nog geen account heeft."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Voornaam"
              value={ownerFirstName}
              onChange={(e) => setOwnerFirstName(e.target.value)}
            />
            <Input
              label="Achternaam"
              value={ownerLastName}
              onChange={(e) => setOwnerLastName(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={saving}>
              Aanmaken
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/platform')}>
              Annuleren
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

export default PlatformOrgNewPage
